// The copy under each game. Kept here so the game files stay as they were.
// Every number in it is true of the games as they are: 15 questions in the
// three quizzes, 11 matchups in Bounty Battle, 11+ in all four for Pirate King.

const otherGames = {
  character: { to: '/quiz', label: 'Character Quiz', note: 'name the character from one picture' },
  image: { to: '/image-quiz', label: 'Image Quiz', note: 'pick the face that matches the name' },
  devilfruit: { to: '/devil-fruit-quiz', label: 'Devil Fruit Quiz', note: 'name the user behind every fruit' },
  bounty: { to: '/bounty-battle', label: 'Bounty Battle', note: 'guess which pirate is worth more' },
  puzzle: { to: '/puzzle', label: 'Jigsaw Puzzle', note: 'put a character back together, 12 to 64 pieces' },
  board: { to: '/leaderboard', label: 'Leaderboard', note: 'see the top run in every game' },
  archive: { to: '/characters', label: 'Character Archive', note: 'profiles, bounties and Devil Fruits' },
};

const pick = (...keys) => keys.map((k) => otherGames[k]);

// Every game, so no page's "Play the rest" leaves one out. QuizPageText adds
// whichever of these a page's own list is missing.
export const ALL_GAMES = ['character', 'image', 'devilfruit', 'bounty', 'puzzle'].map((k) => otherGames[k]);

export const characterQuizText = {
  title: 'One Piece Character Quiz',
  intro: [
    'One picture, four names, fifteen seconds. The Character Quiz shows a face from anywhere in One Piece and asks who it is, from Straw Hats everyone knows to Marines, Warlords and faces from a single arc.',
    'Fifteen questions make a round. Your score and your time are saved together, so two players on the same score are separated by who was quicker.',
  ],
  howItWorks: [
    'Fifteen characters a round, drawn fresh each time, so no two rounds are the same.',
    'Fifteen seconds a question. Running out costs the point, the same as a wrong answer.',
    'Stuck on one? Pass it and keep the clock moving.',
    'At the end you can put your run on the leaderboard with a pirate name. No email, no password to invent.',
  ],
  faq: [
    {
      q: 'How many characters are in the quiz?',
      a: 'Questions are drawn from the full character archive, not a short fixed list, so a round can ask about the Straw Hats, the Marines, the Worst Generation or a character from one arc.',
    },
    {
      q: 'What is a good score?',
      a: 'Eleven out of fifteen is the mark that matters: clear eleven in all four games and you hold the Pirate King title on the leaderboard. A perfect fifteen is decided on time.',
    },
    {
      q: 'Do I need an account to play?',
      a: 'No. You can play every game without one. An account only exists so your score can sit on the leaderboard under a name you choose.',
    },
  ],
  links: pick('image', 'devilfruit', 'bounty', 'board', 'archive'),
};

export const imageQuizText = {
  title: 'One Piece Image Quiz',
  intro: [
    'The Character Quiz gives you a face and asks for the name. The Image Quiz turns it around: you get the name and four pictures, and you pick the right one.',
    'It catches the characters you know by name but have never really looked at, and the ones who look like someone else.',
  ],
  howItWorks: [
    'Fifteen names a round, four pictures each.',
    'Fifteen seconds a question, and the timer keeps running between them.',
    'Your total time is the tie-break, so the leaderboard rewards knowing on sight.',
    'Finish and your run can go straight on the board under a pirate name.',
  ],
  faq: [
    {
      q: 'How is this different from the Character Quiz?',
      a: 'The Character Quiz shows a picture and asks for the name. The Image Quiz gives the name and asks which of four pictures is that character.',
    },
    {
      q: 'Where do the pictures come from?',
      a: 'Official character art from the anime and manga, the same images used across the character archive on this site.',
    },
    {
      q: 'Can I play more than once?',
      a: 'As often as you like. Every run is saved, and the leaderboard keeps your best one while your other runs stay on your name.',
    },
  ],
  links: pick('character', 'devilfruit', 'bounty', 'board', 'archive'),
};

export const devilFruitQuizText = {
  title: 'One Piece Devil Fruit Quiz',
  intro: [
    'Gomu Gomu no Mi, Mera Mera no Mi, Ope Ope no Mi: the fruits are half of what makes a fight in One Piece. This quiz names a Devil Fruit and asks who ate it, across Paramecia, Logia and Zoan.',
    'Fifteen fruits a round. Most players know the Straw Hats, and then the Warlords and the Emperors start deciding the score.',
  ],
  howItWorks: [
    'Fifteen Devil Fruits a round, from famous ones to fruits that appeared in a single fight.',
    'Fifteen seconds a fruit.',
    'Score and time are both saved, so speed breaks a tie at the top.',
    'Eleven or better here is one of the four you need for the Pirate King title.',
  ],
  faq: [
    {
      q: 'Which Devil Fruits does the quiz cover?',
      a: 'All three types: Paramecia, Logia and Zoan, including the Mythical Zoans, and fruits whose users appear for only one arc.',
    },
    {
      q: 'Does it spoil the story?',
      a: 'A question names a fruit and its user, so a fruit revealed late in the story can appear. If you are behind on the anime, the Character Quiz is the safer round.',
    },
    {
      q: 'What counts as a top score here?',
      a: 'A perfect fifteen is common enough that the leaderboard is decided on time, so the fastest clean round takes the top spot.',
    },
  ],
  links: pick('character', 'image', 'bounty', 'board', 'archive'),
};

export const bountyBattleText = {
  title: 'One Piece Higher or Lower: the Bounty Game',
  intro: [
    'Two wanted posters, one question: is the second bounty higher or lower? Bounty Battle is the One Piece higher or lower game, played with the real figures from the story, from rookies worth a few million berries to the Emperors of the Sea.',
    'Eleven matchups a round, and one wrong instinct about a Yonko commander ends it.',
  ],
  howItWorks: [
    'Eleven head-to-head matchups a round, out of eleven.',
    'Every bounty is the amount given in the story, not an estimate.',
    'There is no timer pressure between rounds, but your total time still separates two perfect runs.',
    'Eleven out of eleven is the hardest of the four to hold, because it leaves no room for a single miss.',
  ],
  faq: [
    {
      q: 'Is this the One Piece higher or lower game?',
      a: 'Yes. You see one bounty, then a second character, and you say whether their bounty is higher or lower. Get it right and the next pair comes up.',
    },
    {
      q: 'How many questions are in Bounty Battle?',
      a: 'Eleven matchups, so a perfect run is eleven out of eleven. The other three games are out of fifteen.',
    },
    {
      q: 'Where do the bounties come from?',
      a: 'The figures announced in the story. When a character has more than one bounty over time, the quiz uses their latest known amount.',
    },
    {
      q: 'Is this the hardest game on the site?',
      a: 'It is the one where a perfect run is rarest, because eleven right answers in a row leaves no room for a guess.',
    },
  ],
  links: pick('character', 'image', 'devilfruit', 'board', 'archive'),
};

export const wantedPosterText = {
  title: 'One Piece WANTED Poster Maker',
  intro: [
    'Upload your own photo — or swipe the archive and pick any character — type a pirate name, and the poster is made: the same DEAD OR ALIVE board the Marines nail to every wall in One Piece, with your bounty in berries under your name.',
    'Your photo never leaves your device. Your browser reads the file and draws it, nothing is uploaded, and Download saves the poster as a real image you can post anywhere.',
  ],
  howItWorks: [
    'Upload a photo, or swipe the row of faces and tap anyone in it.',
    'Drag the picture to move it, and use the zoom slider to frame it.',
    'Type your pirate name and an epithet — "Cat Burglar", "Fire Fist", or your own.',
    'Pick your sea and your bounty, then reroll until the number feels right.',
    'Hit Download for the image, or Share to send it straight into a chat.',
  ],
  faq: [
    {
      q: 'Is my photo uploaded anywhere?',
      a: 'No. The poster is drawn inside your browser from the file you picked, and that file never leaves your device — no account, no server, nothing stored.',
    },
    {
      q: 'How do I save it?',
      a: 'Press Download. You get a PNG at 1200x1800, big enough to post or print. On a phone, Share hands the same image straight to WhatsApp, Instagram or X.',
    },
    {
      q: 'Where do the bounties come from?',
      a: 'Each tier covers the range real One Piece bounties fall in: small fry in the single millions, rookies in the tens of millions, Supernovas in the hundreds of millions, Emperors above a billion berries. Or type any number you like.',
    },
    {
      q: 'Can I use a character instead of my face?',
      a: 'Yes — the row under the upload button holds the whole archive. Swipe it sideways and tap anyone from Luffy to Mihawk.',
    },
  ],
  links: [
    { to: '/quiz', label: 'Character Quiz', note: 'name the character from one picture' },
    { to: '/bounty-battle', label: 'Bounty Battle', note: 'guess which pirate is worth more' },
    { to: '/bounties', label: 'Bounty Rankings', note: 'every known bounty, highest first' },
    { to: '/characters', label: 'Character Archive', note: 'profiles, bounties and Devil Fruits' },
  ],
};

export const crewBuilderText = {
  title: 'Build Your One Piece Crew',
  intro: [
    'Add anyone you want, up to ten, from every character in the archive. No jobs to fill and nobody you are forced to pick: a crew of two swordsmen and eight cooks is your business.',
    'Each member carries their real bounty on the card, and the total underneath adds them up — so a crew of Emperors reads very differently from a crew of rookies.',
  ],
  howItWorks: [
    'Add a member, search by name or crew, and tap a face. The bounty is shown while you choose.',
    'The first one on the list is the captain. Move anyone up or down with the arrows.',
    'The card grows with the crew: a few members make a short card, ten make a tall one.',
    'Download saves it as an image; Share sends it straight into a chat.',
  ],
  faq: [
    {
      q: 'How is the bounty total worked out?',
      a: 'It adds up the published bounties of everyone in your crew, taken from the character archive. Anyone with no known bounty adds nothing.',
    },
    {
      q: 'How many can I add?',
      a: 'Between one and ten. The card redraws itself each time, so a crew of four stays compact and a crew of ten runs longer.',
    },
    {
      q: 'Can I pick ten captains?',
      a: 'Yes. Nothing stops you putting Luffy, Shanks, Kaido, Whitebeard, Roger and Big Mom on the same ship — the total will show what that would cost the Marines.',
    },
    {
      q: 'Is my crew saved anywhere?',
      a: 'No. The card is drawn in your browser and nothing is sent anywhere. Download it and it is yours.',
    },
  ],
  links: [
    { to: '/wanted', label: 'WANTED Poster Maker', note: 'put your own face on a bounty' },
    { to: '/quiz', label: 'Character Quiz', note: 'name the character from one picture' },
    { to: '/bounties', label: 'Bounty Rankings', note: 'every known bounty, highest first' },
    { to: '/characters', label: 'Character Archive', note: 'profiles, bounties and Devil Fruits' },
  ],
};

export const whichCharacterText = {
  title: 'Which One Piece Character Are You?',
  intro: [
    'Eight questions about what you want, how you fight, and who you would follow. The answers add up to one of twelve characters: Luffy, Zoro, Nami, Sanji, Usopp, Chopper, Robin, Law, Ace, Shanks, Hancock or Mihawk.',
    'There is no score and no timer. Answer honestly, get a result, and send it to whoever you argue with about this.',
  ],
  howItWorks: [
    'Eight questions, four answers each, no right or wrong.',
    'Every answer adds a point to the characters it fits.',
    'The character with the most points at the end is your match, and ties are broken at random rather than always landing on the captain.',
    'Share the result, or take it again and answer as the person you wish you were.',
  ],
  faq: [
    {
      q: 'How many characters can you get?',
      a: 'Twelve: the Straw Hats plus Law, Ace, Shanks, Boa Hancock and Mihawk.',
    },
    {
      q: 'Can I get a different result?',
      a: 'Yes. Different answers lead to different characters, and when two tie the quiz picks between them rather than always giving the same one.',
    },
    {
      q: 'Is this a knowledge quiz?',
      a: 'No. This one is about you. For questions with right answers, the Character Quiz and the Devil Fruit Quiz are the ones with a leaderboard.',
    },
  ],
  links: [
    { to: '/quiz', label: 'Character Quiz', note: 'name the character from one picture' },
    { to: '/image-quiz', label: 'Image Quiz', note: 'pick the face that matches the name' },
    { to: '/devil-fruit-quiz', label: 'Devil Fruit Quiz', note: 'name the user behind every fruit' },
    { to: '/characters', label: 'Character Archive', note: 'profiles, bounties and Devil Fruits' },
  ],
};

export const comparePageText = {
  title: 'Compare One Piece Characters',
  intro: [
    'Arguments about One Piece usually come down to numbers nobody has in front of them. This puts two characters side by side with the real ones: bounty, crew, role and Devil Fruit, with the bigger bounty marked.',
    'Pick any two of the 118 characters in the archive — a Straw Hat against an Emperor, two Supernovas from the same generation, or a Marine admiral against the pirate he hunted.',
  ],
  howItWorks: [
    'Search either side by name or crew; the list filters as you type.',
    'Bounties are the figures announced in the story, and the higher one is highlighted.',
    'A character with no bounty shows "Unknown" rather than a number somebody invented.',
    'Change one side and the comparison updates — no page reload, no limit on how many you try.',
  ],
  faq: [
    {
      q: 'Who has the highest bounty in One Piece?',
      a: 'Gol D. Roger, at 5,564,800,000 berries. Among the living, the Emperors sit at the top — compare them here and see the gap for yourself.',
    },
    {
      q: 'Why does a character show no bounty?',
      a: 'Marines, civilians and some allies were never given one, and a few pirates have never had a figure revealed. The page says so instead of guessing.',
    },
    {
      q: 'Does a bigger bounty mean a stronger fighter?',
      a: 'Not exactly. A bounty is what the World Government thinks you threaten, so it rises with what you destroy and who you embarrass, not only with strength.',
    },
  ],
  links: [
    { to: '/bounties', label: 'Bounty Rankings', note: 'every known bounty, highest first' },
    { to: '/bounty-battle', label: 'Bounty Battle', note: 'the higher or lower bounty game' },
    { to: '/characters', label: 'Character Archive', note: 'profiles, crews and Devil Fruits' },
    { to: '/quiz', label: 'Character Quiz', note: 'name the character from one picture' },
  ],
};

export const leaderboardPageText = {
  title: 'The One Piece Quiz Leaderboard',
  intro: [
    'Every run saved from the four games lands here: the character quiz, the image quiz, the Devil Fruit quiz and Bounty Battle. Each tab ranks by score first, then by time, so a faster clean round beats a slower one.',
    'The Hall of Legends is the hard one. Clear eleven or better in all four games and you hold the Pirate King title, with your four scores and your total time on show.',
  ],
  howItWorks: [
    'One row per player: their best run, with a tag showing how many times they have played that game.',
    'Tap a player to see every run they posted, best first.',
    'The strip above the table says who holds number one, how long they have held it, and how many runs failed to take it off them.',
    'Saving a score takes a pirate name and nothing else — no email, no password to invent.',
  ],
  faq: [
    {
      q: 'How do I get on the leaderboard?',
      a: 'Finish any of the four games and save the run with a pirate name. The account is made for you on the spot and you stay signed in on that device.',
    },
    {
      q: 'What is a Pirate King here?',
      a: 'A player who has scored eleven or better in all four games. It is worked out from live scores, so it is earned and it can be lost.',
    },
    {
      q: 'Why is someone ahead of me on the same score?',
      a: 'Time breaks the tie. Two perfect rounds are separated by who finished faster.',
    },
  ],
  links: [
    { to: '/quiz', label: 'Character Quiz', note: 'name the character from one picture' },
    { to: '/image-quiz', label: 'Image Quiz', note: 'pick the face that matches the name' },
    { to: '/devil-fruit-quiz', label: 'Devil Fruit Quiz', note: 'name the user behind every fruit' },
    { to: '/bounty-battle', label: 'Bounty Battle', note: 'the higher or lower bounty game' },
  ],
};

export const guidesPageText = {
  title: 'One Piece Guides',
  intro: [
    'Three guides, written to answer the questions fans actually ask, with the detail you would otherwise dig out of a dozen wiki pages.',
    'They are short on purpose: the crew, the fruits and the people who matter, then a quiz to see what stuck.',
  ],
  howItWorks: [
    'The Straw Hat guide: all ten, their job on the ship, and the arc each one joined in.',
    'The Devil Fruit guide: Paramecia, Zoan and Logia, why a Logia body is so hard to hit, and what awakening changes.',
    'The character guide: the Emperors, the Warlords, the Marine admirals and the rivals who keep coming back.',
    'Every guide ends with a link into the quiz that covers it.',
  ],
  faq: [
    {
      q: 'How many Devil Fruit types are there?',
      a: 'Three: Paramecia, Zoan and Logia. The Devil Fruit guide explains what each one does to the body and which famous fruits belong where.',
    },
    {
      q: 'Who are the Straw Hat Pirates?',
      a: 'Luffy\'s crew — ten of them by the latest arc, from the swordsman Zoro to the shipwright Franky. The crew guide lists every role and when each joined.',
    },
    {
      q: 'Do I need to read the guides to play?',
      a: 'No. The games stand on their own; the guides are for the parts you want to learn before trying again.',
    },
  ],
  links: [
    { to: '/guides/straw-hat-crew', label: 'The Straw Hat Pirates', note: 'all ten, and what each one does' },
    { to: '/guides/devil-fruits', label: 'Devil Fruits Explained', note: 'the three types and the famous fruits' },
    { to: '/guides/characters', label: 'Allies, Enemies and Legends', note: 'Emperors, Warlords and Marines' },
    { to: '/devil-fruit-quiz', label: 'Devil Fruit Quiz', note: 'name the user behind every fruit' },
  ],
};
